import DoneIcon from "@NA/Icons/DoneIcon";
import InProgressIcon from "@NA/Icons/InProgressIcon";
import MyAccountBillingIcon from "@NA/Icons/MyAccountBillingIcon";
import ReadyForPickupIcon from "@NA/Icons/ReadyForPickupIcon";
import TruckIcon from "@NA/Icons/TruckIcon";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import { StyledButton } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";
import ADIOrderDetailsProgressBar from "./ADIOrderDetailsProgressBar";

interface OwnProps {
    shipment: any;
    userRoles: string;
    order?: OrderModel;
}

export interface AdiOrderDetailsDeliveryInfoStyles {
    progressBarGridItemMobile?: GridItemProps;
    wrapper?: InjectableCss;
    orderDate?: TypographyProps;
    invoiceNumWrapper?: InjectableCss;
    deliveryInfoGridOne?: GridItemProps;
    deliveryInfoGridTwo?: GridItemProps;
    deliveryInfoGridTwoMobile?: GridItemProps;
    deliveryInfoGridThree?: GridItemProps;
    deliveryInfoGridThreeMobile?: GridItemProps;
    billingIconStyles?: IconPresentationProps;
    billingIconStylesMobile?: IconPresentationProps;
    deliveryStatusStyles?: IconPresentationProps;
    deliveryStatusMobileStyles?: IconPresentationProps;
    invoiceNumFullText?: InjectableCss;
    invoiceNumSmallText?: InjectableCss;
    dateStylesMobile?: InjectableCss;
    invoiceNumText?: InjectableCss;
    iconContainer?: InjectableCss;
    iconContainerText?: InjectableCss;
    itemNumbersDiv?: InjectableCss;
    itemNumbersDivTwo?: InjectableCss;
    multipleShipsText?: InjectableCss;
    deliveryInfoContainer?: GridContainerProps;
    displayNoneMobilePrint?: InjectableCss;
    shippingStatusStyles?: InjectableCss;
    trackingLinkStyles?: InjectableCss;
    invoiceStatus?: InjectableCss;
}

type Props = OwnProps;

export const adiOrderDetailsDeliveryInfoStyles: AdiOrderDetailsDeliveryInfoStyles = {
    trackingLinkStyles: {
        css: css`
            padding-left: 28px;
            > ${TypographyStyle} {
                display: block;
            }
            > ${StyledButton} {
                > ${TypographyStyle} {
                    font-size: 14px;
                }
            }
        `,
    },
    shippingStatusStyles: {
        css: css`
            display: flex;
            align-items: center;
            > ${TypographyStyle} {
                padding-left: 8px;
            }
        `,
    },
    progressBarGridItemMobile: {
        width: 12,
        printWidth: 0,
        css: css`
            display: none;
            @media only screen and (max-width: 1024px) {
                display: flex;
            }
            @media print {
                .rd-orders-details-landing {
                    display: none;
                }
            }
        `,
    },
    displayNoneMobilePrint: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
    deliveryInfoContainer: {
        css: css`
            width: 100%;
            @media print {
                width: 100%;
            }
        `,
    },
    multipleShipsText: {
        css: css`
            display: none;
            @media print {
                display: none;
            }
            @media screen and (min-width: 576px) and (max-width: 1024px) {
                display: inline-block;
            }
        `,
    },
    itemNumbersDiv: {
        css: css`
            @media print {
                display: none;
            }
            display: none;
            @media only screen and (max-width: 1024px) {
                display: flex;
                flex-direction: column;

                .trackingText {
                    display: none;
                }
                .trackingText:first-child {
                    display: block;
                    padding-right: 10px;
                }
            }
        `,
    },
    itemNumbersDivTwo: {
        css: css`
            @media print {
                display: none;
            }
            .trackingText {
                @media print {
                    display: none;
                }
            }
            display: none;
            @media only screen and (max-width: 1024px) {
                display: flex;
                justify-content: flex-end;
            }
        `,
    },
    deliveryInfoGridThreeMobile: {
        width: 12,
        css: css`
            display: none;
            @media screen and (min-width: 576px) and (max-width: 1024px) {
                display: flex;
                justify-content: space-between;
            }
        `,
    },
    iconContainerText: {
        css: css`
            @media print {
                display: none;
            }
            > ${TypographyStyle} {
                font-size: 14px;
                text-align: right;
                display: flex;
                max-width: 240px;
            }
        `,
    },
    iconContainer: {
        css: css`
            @media print {
                display: none;
            }
            display: flex;
            align-items: center;
            padding-right: 10px;
            > ${IconWrapper} {
                padding-right: 8px;
                width: 28px;
            }
            > ${TypographyStyle} {
                font-size: 14px;
                color: ${ADIColors.darkBlue1};
            }
        `,
    },
    invoiceNumText: {
        css: css`
            @media only screen and (max-width: 1024px) {
                padding-right: 16px;
            }
            @media print {
                padding-right: 0px;
            }
        `,
    },
    dateStylesMobile: {
        css: css`
            display: none;
            @media only screen and (max-width: 1024px) {
                display: inline-block;
                font-size: 14px;
                font-weight: bold;
                line-height: 1.43;
                color: ${ADIColors.darkBlue1};
                margin-top: 2px;
            }
            @media print {
                display: none;
            }
        `,
    },
    invoiceNumFullText: {
        css: css`
            @media print {
                color: ${ADIColors.black};
                font-size: 12px;
                word-break: break-word;
                display: inline-block;
            }
            display: block;
            @media only screen and (max-width: 1024px) {
                display: none;
            }
        `,
    },
    invoiceNumSmallText: {
        css: css`
            display: none;
            @media only screen and (max-width: 1024px) {
                display: inline-block;
                padding: 0px 14px;
            }
            @media print {
                display: none;
            }
        `,
    },
    wrapper: {
        css: css`
            @media only screen and (max-width: 1024px) {
                width: 100%;
            }
        `,
    },
    deliveryInfoGridOne: {
        width: 12,
        printWidth: 12,
        css: css`
            padding-bottom: 10px;
            > ${IconWrapper} {
                padding-right: 8px;
                width: 28px;
                @media print {
                    padding-right: 4px;
                    width: 20px;
                }
            }
            @media screen and (min-width: 576px) and (max-width: 1024px) {
                align-items: flex-start;
                flex-basis: 50%;
                max-width: 50%;
            }
            @media only screen and (max-width: 576px) {
                justify-content: space-between;
            }
        `,
    },
    deliveryInfoGridTwo: {
        width: 12,
        printWidth: 12,
        css: css`
            padding-top: 16px;
            padding-bottom: 0px;
            @media print {
                align-items: center;
                padding-top: 10px;
            }
            > ${IconWrapper} {
                padding-right: 8px;
                width: 28px;
                @media print {
                    padding-right: 4px;
                    width: 20px;
                    padding-top: 10px;
                }
            }
            > ${TypographyStyle} {
                font-size: 14px;
                font-weight: bold;
                line-height: 20px;
                color: ${ADIColors.darkBlue1};
                @media print {
                    color: ${ADIColors.black};
                    font-size: 12px;
                }
            }
            @media only screen and (max-width: 1024px) {
                display: none;
            }
        `,
    },
    deliveryInfoGridTwoMobile: {
        width: 6,
        css: css`
            display: none;
            @media print {
                display: none;
            }
            @media only screen and (max-width: 1024px) {
                display: flex;
                justify-content: flex-end;
            }
            @media only screen and (max-width: 576px) {
                justify-content: space-between;
                flex-basis: 100%;
                max-width: 100%;
                padding-bottom: 0px;
            }
        `,
    },
    deliveryInfoGridThree: {
        width: 12,
        printWidth: 12,
        css: css`
            padding-top: 0px;
            flex-direction: column;
            > ${TypographyStyle} {
                padding-left: 29px;
                padding-top: 8px;
                font-size: 14px;
                line-height: 20px;
                color: ${getColor("text.main")};
                @media print {
                    color: ${ADIColors.black};
                    font-size: 12px;
                    padding-left: 0px;
                }
            }
            @media only screen and (max-width: 1024px) {
                display: none;
            }
        `,
    },
    invoiceStatus: {
        css: css`
            font-weight: normal !important;
            }`,
    },
    invoiceNumWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            @media only screen and (max-width: 1024px) {
                flex-direction: row;
                align-items: center;
            }
            > ${TypographyStyle} {
                font-size: 14px;
                font-weight: bold;
                line-height: 16px;
                color: ${ADIColors.darkBlue1};
                @media print {
                    color: ${ADIColors.black};
                    font-size: 12px;
                }
                &:last-child {
                    padding-top: 8px;
                    @media only screen and (max-width: 1024px) {
                        padding-top: 0px;
                    }
                }
            }
            > ${StyledButton} {
                > ${TypographyStyle} {
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 14px;
                    color: ${getColor("text.link")};
                    padding-top: 8px;
                    @media only screen and (max-width: 1024px) {
                        padding-right: 16px;
                    }
                    @media print {
                        font-size: 12px;
                        color: ${ADIColors.black};
                    }
                }
            }
        `,
    },
    billingIconStyles: {
        css: css`
            height: 24px;
            width: 18px;
            display: block;
            @media only screen and (max-width: 1024px) {
                display: none;
            }
        `,
    },
    billingIconStylesMobile: {
        css: css`
            height: 24px;
            width: 18px;
            display: none;
            @media only screen and (max-width: 1024px) {
                display: inline-block;
            }
            @media print {
                display: none;
            }
        `,
    },
    deliveryStatusStyles: {
        css: css`
            height: 20px;
            width: 20px;
        `,
    },
    deliveryStatusMobileStyles: {
        css: css`
            height: 20px;
            width: 20px;
            @media print {
                display: none;
            }
        `,
    },
};

const styles = adiOrderDetailsDeliveryInfoStyles;

const ADIOrderDetailsDeliveryInfo: FC<Props> = ({ userRoles, shipment, order }) => {
    const isUserRoleForInvoice = userRoles?.includes("Accounting");

    const getShippingStatus = (shipment: any) => {
        let suStatus = "Shipped";
        if (
            shipment?.trackingList[0]?.shipActivities !== null &&
            shipment?.trackingList[0]?.shipActivities.length > 0
        ) {
            shipment?.trackingList[0]?.shipActivities.forEach((i: any) => {
                if (i?.statusText === "Delivered") {
                    suStatus = "Delivered";
                }
            });
            return suStatus;
        }
    };

    const formattrackingUrl = (trackingNumber: string, trackingUrl: string) => {
        const urlArray = trackingUrl.split(",");
        for (let i = 0; i < urlArray.length; i++) {
            const urlIndex = urlArray[i].indexOf(trackingNumber);
            if (urlIndex > -1) {
                return urlArray[i];
            }
        }
    };

    const goTotrackingUrl = (trackingNumber: string, trackingUrl: string) => {
        window.open(formattrackingUrl(trackingNumber, trackingUrl), "_blank");
    };

    const uniqueTrackingNumbers = Array.from(
        new Set(shipment?.invoiceOrderLines.map((line: any) => line?.properties["trackingNumber"])),
    );

    const inputDate = new Date(order?.orderDate!);
    const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
    const formattedDate = inputDate.toLocaleDateString("en-US", options).replace(" ", ". ");

    const totalShips: any = uniqueTrackingNumbers.map((a: any) => a.split(",").length);

    const deliveryInfoThreeCustomStyle = () => css`
        justify-content: ${totalShips > 2 ? "space-between" : "flex-end"};
        @media only screen and (max-width: 576px) {
            justify-content: flex-start;
        }
        @media print {
            display: none;
        }
    `;

    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.deliveryInfoContainer}>
                <GridItem {...styles.deliveryInfoGridOne}>
                    <Icon src={MyAccountBillingIcon} {...styles.billingIconStyles} />
                    <StyledWrapper {...styles.invoiceNumWrapper}>
                        <Icon src={MyAccountBillingIcon} {...styles.billingIconStylesMobile} />
                        <Typography {...styles.invoiceNumFullText}>{translate("Invoice Number")}</Typography>
                        <Typography {...styles.invoiceNumSmallText}>{translate("Invoice #")}</Typography>
                        {/* conditions added in US Spire ----- P21 Change */}
                        {(isUserRoleForInvoice && shipment?.invoiceNumber && 
                          shipment?.invoiceNumber?.toLowerCase() !== 'pending shipment') ? (
                                <Link
                                    id={`invoiceListViewInvoiceNoLink_${shipment.invoiceNumber}`}
                                    href={`/MyAccount/Invoices/Details?invoiceNumber=${shipment.invoiceNumber}&InvType=${shipment.suInvType}`}
                                    target="_blank"
                                >
                                    {shipment.invoiceNumber}
                                </Link>
                            ) : (
                                <Typography {...styles.invoiceStatus}>{shipment?.invoiceNumber}</Typography>
                            )
                        }
                    </StyledWrapper>
                    <Typography {...styles.dateStylesMobile}>{formattedDate}</Typography>
                </GridItem>
                <GridItem {...styles.deliveryInfoGridTwo}>
                    {(shipment?.suStatus !== "Shipped" ||
                        (shipment?.suStatus === "Shipped" &&
                            (shipment?.trackingList.length === 0 || order?.properties?.isLTL === "Y"))) && (
                        <>
                            {shipment?.suStatus === "Shipped" && (
                                <Icon src={TruckIcon} {...styles.deliveryStatusStyles} />
                            )}
                            {shipment?.suStatus === "Ready for Pickup" && (
                                <Icon src={ReadyForPickupIcon} {...styles.deliveryStatusStyles} />
                            )}
                            {shipment?.suStatus === "Picked Up" && (
                                <Icon src={DoneIcon} {...styles.deliveryStatusStyles} />
                            )}
                            {shipment?.suStatus === "Preparing Order" && (
                                <Icon src={InProgressIcon} {...styles.deliveryStatusStyles} />
                            )}
                            {shipment?.suStatus === "Pending" && (
                                <Icon src={InProgressIcon} {...styles.deliveryStatusStyles} />
                            )}
                            <Typography>{shipment?.suStatus}</Typography>
                        </>
                    )}
                </GridItem>
                <GridItem {...styles.deliveryInfoGridTwoMobile}>
                    <StyledWrapper {...styles.iconContainer}>
                        {shipment?.suStatus === "Shipped" && (
                            <Icon src={TruckIcon} {...styles.deliveryStatusMobileStyles} />
                        )}
                        {shipment?.suStatus === "Ready for Pickup" && (
                            <Icon src={ReadyForPickupIcon} {...styles.deliveryStatusMobileStyles} />
                        )}
                        {shipment?.suStatus === "Picked Up" && (
                            <Icon src={DoneIcon} {...styles.deliveryStatusMobileStyles} />
                        )}
                        {shipment?.suStatus === "Preparing Order" && (
                            <Icon src={InProgressIcon} {...styles.deliveryStatusMobileStyles} />
                        )}
                        {shipment?.suStatus === "Pending" && (
                            <Icon src={InProgressIcon} {...styles.deliveryStatusMobileStyles} />
                        )}
                        {shipment?.suStatus !== "Shipped" ||
                            (shipment?.suStatus === "Shipped" &&
                                (shipment?.trackingList.length === 0 || order?.properties?.isLTL === "Y") && (
                                    <Typography {...styles.displayNoneMobilePrint}>{shipment?.suStatus}</Typography>
                                ))}
                        {shipment?.suStatus === "Shipped" &&
                            shipment?.trackingList.length > 0 &&
                            order?.properties?.isLTL !== "Y" && (
                                <Typography {...styles.displayNoneMobilePrint}>
                                    {getShippingStatus(shipment)}
                                </Typography>
                            )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.iconContainerText}>
                        {shipment?.suStatus === "Pending" && (
                            <Typography {...styles.displayNoneMobilePrint}>
                                {shipment?.deliveryMethod === "PICK UP"
                                    ? translate("We’ll send you an email when your order is ready for pickup.")
                                    : translate("We’ll send you an email when your order is shipped.")}
                            </Typography>
                        )}
                        {shipment?.suStatus === "Preparing Order" && (
                            <Typography {...styles.displayNoneMobilePrint}>
                                {translate(
                                    "Your order is being attended to for shipment by the manufacturer or vendor.",
                                )}
                            </Typography>
                        )}
                        {shipment?.suStatus === "Picked Up" && (
                            <Typography {...styles.displayNoneMobilePrint}>
                                {shipment?.deliveryFrom.toUpperCase().trim() === "DALLAS CC"
                                    ? shipment?.deliveryFrom.toUpperCase().replace(" CC", "")
                                    : shipment?.deliveryFrom.toUpperCase()}{" "}
                                {translate("Branch")}
                            </Typography>
                        )}
                        {shipment?.suStatus === "Ready for Pickup" && (
                            <Typography {...styles.displayNoneMobilePrint}>
                                {shipment?.deliveryFrom.toUpperCase().trim() === "DALLAS CC"
                                    ? shipment?.deliveryFrom.toUpperCase().replace(" CC", "")
                                    : shipment?.deliveryFrom.toUpperCase()}{" "}
                                {translate("Branch")}
                            </Typography>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.deliveryInfoGridThree}>
                    {shipment?.suStatus === "Shipped" &&
                        (shipment?.trackingList.length === 0 || order?.properties?.isLTL === "Y") && (
                            <>
                                {uniqueTrackingNumbers?.map((shipmentPackageLine: any, index: number) => (
                                    <>
                                        {shipmentPackageLine.split(",").length > 2 && (
                                            <Typography>
                                                {translate("This shipment is divided into multiple cartons.")}
                                            </Typography>
                                        )}
                                        {shipmentPackageLine.split(",").map((trackingNumber: any) => (
                                            <>
                                                <Typography>{trackingNumber !== "" && trackingNumber}</Typography>
                                            </>
                                        ))}
                                    </>
                                ))}
                            </>
                        )}
                    {shipment?.suStatus === "Ready for Pickup" && (
                        <Typography>
                            {shipment?.deliveryFrom.toUpperCase().trim() === "DALLAS CC"
                                ? shipment?.deliveryFrom.toUpperCase().replace(" CC", "")
                                : shipment?.deliveryFrom.toUpperCase()}{" "}
                            {translate("Branch")}
                        </Typography>
                    )}
                    {shipment?.suStatus === "Picked Up" && (
                        <Typography>
                            {shipment?.deliveryFrom.toUpperCase().trim() === "DALLAS CC"
                                ? shipment?.deliveryFrom.toUpperCase().replace(" CC", "")
                                : shipment?.deliveryFrom.toUpperCase()}{" "}
                            {translate("Branch")}
                        </Typography>
                    )}
                    {shipment?.suStatus === "Pending" && (
                        <Typography>
                            {shipment.deliveryMethod === "PICK UP"
                                ? translate("We’ll send you an email when your order is ready for pickup.")
                                : translate("We’ll send you an email when your order is shipped.")}
                        </Typography>
                    )}
                    {shipment?.suStatus === "Preparing Order" && (
                        <Typography>
                            {translate("Your order is being attended to for shipment by the manufacturer or vendor.")}
                        </Typography>
                    )}
                    {shipment?.suStatus === "Shipped" &&
                        shipment?.trackingList.length > 0 &&
                        order?.properties?.isLTL !== "Y" && (
                            <>
                                <StyledWrapper {...styles.shippingStatusStyles}>
                                    {getShippingStatus(shipment) === "Shipped" && (
                                        <Icon src={TruckIcon} {...styles.deliveryStatusStyles} />
                                    )}
                                    {getShippingStatus(shipment) === "Delivered" && (
                                        <Icon src={DoneIcon} {...styles.deliveryStatusStyles} />
                                    )}
                                    <Typography>{getShippingStatus(shipment)}</Typography>
                                </StyledWrapper>
                                {uniqueTrackingNumbers.map((shipmentPackageLine: any) => (
                                    <StyledWrapper {...styles.trackingLinkStyles}>
                                        {shipmentPackageLine.split(",").length > 2 && (
                                            <Typography>
                                                {translate("This shipment is divided into multiple cartons.")}
                                            </Typography>
                                        )}
                                        <>
                                            {shipmentPackageLine
                                                .split(",")
                                                .map((trackingNumber: any, index: number) => (
                                                    <>
                                                        {index === 0 && trackingNumber !== "" && (
                                                            <>
                                                                {shipment?.invoiceOrderLines[0]?.properties[
                                                                    "trackingUrl"
                                                                ] !== undefined &&
                                                                    shipment?.invoiceOrderLines[0]?.properties[
                                                                        "trackingUrl"
                                                                    ]
                                                                        .toLowerCase()
                                                                        .indexOf("ups") !== -1 && (
                                                                        <Typography className="trackingText">
                                                                            {translate("UPS")}
                                                                        </Typography>
                                                                    )}
                                                                {shipment?.invoiceOrderLines[0]?.properties[
                                                                    "trackingUrl"
                                                                ] !== undefined &&
                                                                    shipment?.invoiceOrderLines[0]?.properties[
                                                                        "trackingUrl"
                                                                    ]
                                                                        .toLowerCase()
                                                                        .indexOf("fedex") !== -1 && (
                                                                        <Typography className="trackingText">
                                                                            {translate("Fedex")}
                                                                        </Typography>
                                                                    )}
                                                            </>
                                                        )}
                                                        {trackingNumber != "" && (
                                                            <>
                                                                <Link
                                                                    style={{ whiteSpace: "nowrap" }}
                                                                    onClick={() =>
                                                                        goTotrackingUrl(
                                                                            trackingNumber,
                                                                            shipment?.invoiceOrderLines[0]?.properties[
                                                                                "trackingUrl"
                                                                            ],
                                                                        )
                                                                    }
                                                                >
                                                                    <Typography>{trackingNumber}</Typography>
                                                                </Link>
                                                            </>
                                                        )}
                                                    </>
                                                ))}
                                        </>
                                    </StyledWrapper>
                                ))}
                            </>
                        )}
                </GridItem>
                <GridItem {...styles.deliveryInfoGridThreeMobile} css={deliveryInfoThreeCustomStyle()}>
                    {shipment?.suStatus === "Shipped" && (
                        <>
                            {uniqueTrackingNumbers.map((shipmentPackageLine: any) => (
                                <>
                                    {shipmentPackageLine.split(",").length > 2 && (
                                        <Typography {...styles.multipleShipsText}>
                                            {translate("This shipment is divided into multiple cartons.")}
                                        </Typography>
                                    )}
                                    <StyledWrapper {...styles.itemNumbersDiv}>
                                        {shipmentPackageLine.split(",").map((trackingNumber: any, index: number) => (
                                            <StyledWrapper {...styles.itemNumbersDivTwo}>
                                                {index === 0 && trackingNumber !== "" && (
                                                    <>
                                                        {shipment?.invoiceOrderLines.map(
                                                            (shipmentPackageLine2: any) => (
                                                                <>
                                                                    {shipmentPackageLine2?.properties["trackingUrl"] !==
                                                                        undefined &&
                                                                        shipmentPackageLine2?.properties["trackingUrl"]
                                                                            .toLowerCase()
                                                                            .indexOf("ups") !== -1 && (
                                                                            <Typography className="trackingText">
                                                                                {translate("UPS Tracking #")}
                                                                            </Typography>
                                                                        )}
                                                                    {shipmentPackageLine2?.properties["trackingUrl"] !==
                                                                        undefined &&
                                                                        shipmentPackageLine2?.properties["trackingUrl"]
                                                                            .toLowerCase()
                                                                            .indexOf("fedex") !== -1 && (
                                                                            <Typography className="trackingText">
                                                                                {translate("Fedex Tracking #")}
                                                                            </Typography>
                                                                        )}
                                                                </>
                                                            ),
                                                        )}
                                                    </>
                                                )}
                                                {trackingNumber !== "" &&
                                                    shipment?.trackingList.length > 0 &&
                                                    order?.properties?.isLTL !== "Y" && (
                                                        <>
                                                            {shipment?.invoiceOrderLines.map(
                                                                (shipmentPackageLine3: any) => (
                                                                    <>
                                                                        <Link
                                                                            href="javascript:void(0)"
                                                                            onClick={() =>
                                                                                goTotrackingUrl(
                                                                                    trackingNumber,
                                                                                    shipmentPackageLine3?.properties[
                                                                                        "trackingUrl"
                                                                                    ],
                                                                                )
                                                                            }
                                                                        >
                                                                            <Typography
                                                                                {...styles.displayNoneMobilePrint}
                                                                            >
                                                                                {trackingNumber}
                                                                            </Typography>
                                                                        </Link>
                                                                    </>
                                                                ),
                                                            )}
                                                        </>
                                                    )}
                                                {trackingNumber !== "" &&
                                                    (shipment?.trackingList.length === 0 ||
                                                        order?.properties?.isLTL === "Y") && (
                                                        <Typography {...styles.displayNoneMobilePrint}>
                                                            {trackingNumber}
                                                        </Typography>
                                                    )}
                                            </StyledWrapper>
                                        ))}
                                    </StyledWrapper>
                                </>
                            ))}
                        </>
                    )}
                </GridItem>
                <GridItem {...styles.progressBarGridItemMobile}>
                    <ADIOrderDetailsProgressBar shipment={shipment} order={order} />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

export default ADIOrderDetailsDeliveryInfo;
